import MuiTextField from '@mui/material/TextField';
import { TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField/TextField';
import {
  ChangeEvent,
  ClipboardEvent,
  FC,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { InfoIconPopover, InfoIconPopoverProps } from '../InfoIconPopover';
import { Typography } from '../Typography';
import { defaultTheme } from '@automata/mui-theme';
import { styled } from '@automata/mui-theme';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import isNil from 'lodash/isNil';
import { FieldError } from '../FieldError';

const Asterisk = styled('span')`
  color: ${(p) => p.theme.palette.error.main};
  vertical-align: super;
`;

const integerRegex = /^-?\d*$/;
const floatRegex = /^-?\d*\.?\d*$/;

export interface NumberFieldProps
  extends Omit<
      MuiTextFieldProps,
      'error' | 'popover' | 'size' | 'type' | 'onChange'
    >,
    Partial<InfoIconPopoverProps> {
  decimal?: boolean;
  error?: string | boolean | null;
  inputHelpText?: string;
  label?: string;
  min?: number;
  max?: number;
  step?: number;
  value?: number | string | null;
  onChange?: (value: number | null) => void;
}

export const NumberField: FC<NumberFieldProps> = ({
  'aria-label': ariaLabel,
  decimal,
  error,
  id = `numberField-${uuidv4()}`,
  inputHelpText,
  infoPopover,
  infoPopoverMaxWidth,
  infoPopoverAnchorOrigin,
  infoPopoverTransformOrigin,
  label,
  min,
  max,
  required,
  step,
  value,
  onChange,
  ...props
}) => {
  const formattedValue = isNil(value) ? '' : value;
  const [state, setState] = useState<number | string>(formattedValue);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  useEffect(() => {
    if (!isFocused && formattedValue !== state) {
      // allow dynamically change the value
      setState(formattedValue);
    }
  }, [formattedValue, isFocused, state]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;

      if (!(decimal ? floatRegex : integerRegex).test(raw)) return;
      setState(raw);

      const parsed = Number(raw);

      if (raw.trim() !== '' && !Number.isNaN(parsed)) {
        onChange?.(parsed);
      } else {
        onChange?.(null);
      }
    },
    [decimal, onChange]
  );

  const handleBlur = useCallback(() => {
    setIsFocused(false);

    const trimmed = typeof state === 'string' ? state.trim() : String(state);
    const parsed = Number(trimmed);
    if (trimmed === '') {
      setState('');
    } else {
      setState(String(parsed));
    }
  }, [state]);

  const handleFocus = useCallback(() => setIsFocused(true), []);

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedValue = e.clipboardData.getData('text/plain');
      let formattedValue = pastedValue.replaceAll('e', '').trim();

      if (!decimal) {
        formattedValue = formattedValue.replaceAll('.', '');
      }

      setState(formattedValue);
      const parsed = Number(formattedValue);
      onChange?.(Number.isNaN(parsed) ? null : parsed);
    },
    [decimal, onChange]
  );

  return (
    <Stack width={props.fullWidth ? '100%' : 'inherit'}>
      {(label || infoPopover) && (
        <Typography
          alignItems="center"
          color={defaultTheme.palette.text.primary}
          component="label"
          lineHeight={2}
          display="flex"
          gap={0.5}
          htmlFor={id}
          pb={0.5}
          pt={1}
          variant="overline"
        >
          {label}
          {required && <Asterisk>*</Asterisk>}
          {infoPopover && (
            <InfoIconPopover
              infoPopover={infoPopover}
              infoPopoverMaxWidth={infoPopoverMaxWidth}
              infoPopoverAnchorOrigin={infoPopoverAnchorOrigin}
              infoPopoverTransformOrigin={infoPopoverTransformOrigin}
            />
          )}
        </Typography>
      )}
      <FormControl>
        <MuiTextField
          {...props}
          error={!!error}
          id={id}
          label={inputHelpText}
          placeholder={
            decimal ? 'Float (eg: 1.0, 2.5, etc)' : 'Integer (eg: 1, 2, 3 etc)'
          }
          required={!!required}
          size="small"
          slotProps={{ htmlInput: { min, max, step, 'aria-label': ariaLabel } }}
          type="text"
          value={state}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onPaste={handlePaste}
        />
      </FormControl>
      <FieldError error={typeof error === 'string' ? error : null} />
    </Stack>
  );
};
